/**
 * GestorCalendarPage — G1: Temporadas e Fases do calendário esportivo.
 *
 * O Gestor governa o calendário: cria temporada, define fases (schedule_blocks)
 * por escopo CLUB / CATEGORY / GROUP(squad). Coach/Prep/Nutri/Atleta consomem
 * a fase resolvida — não redefinem.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AZURE } from '../../services/api';
import {
  getSeasons, createSeason, getCalendarBlocks, createCalendarBlock,
  cancelCalendarBlock, PHASE_TYPES, PHASE_LABELS, PHASE_COLORS,
} from '../../services/gestorCalendarService';

const THEME = { primary: '#064e3b', gold: '#b48c2c', bg: '#f8fafc' };
const SCOPE_LABELS = { CLUB: 'Clube', CATEGORY: 'Categoria', GROUP: 'Grupo/Squad', TEAM: 'Clube (legado)' };

const fmt = (d) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '—');

const GestorCalendarPage = () => {
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [squads, setSquads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSeasonForm, setShowSeasonForm] = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [seasonForm, setSeasonForm] = useState({ label: '', start_date: '', end_date: '' });
  const [blockForm, setBlockForm] = useState({
    block_name: '', block_type: 'BASE', start_date: '', end_date: '',
    scope: 'CLUB', scope_ref: '', objectives: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getSeasons();
      setSeasons(s);
      const current = seasonId || s[0]?.season_id || null;
      setSeasonId(current);
      setBlocks(await getCalendarBlocks(current || undefined));
    } catch (e) {
      setError(e?.response?.data?.error || 'Erro ao carregar calendário');
    } finally {
      setLoading(false);
    }
  }, [seasonId]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (seasonId === null) return;
    getCalendarBlocks(seasonId).then(setBlocks).catch(() => {});
  }, [seasonId]);

  // Opções de escopo (squads e categorias do clube)
  useEffect(() => {
    AZURE.get('/squads').then(({ data }) => setSquads(data.data || data.squads || []))
      .catch(() => setSquads([]));
    AZURE.get('/gestor/calendar/categories').then(({ data }) => setCategories(data.data || data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const season = useMemo(() => seasons.find(s => s.season_id === seasonId) || null, [seasons, seasonId]);
  const activeBlocks = useMemo(
    () => blocks.filter(b => b.status !== 'cancelled'),
    [blocks]
  );
  const byScope = useMemo(() => {
    const g = { CLUB: [], CATEGORY: [], GROUP: [] };
    activeBlocks.forEach(b => {
      const key = b.scope === 'TEAM' ? 'CLUB' : b.scope;
      (g[key] = g[key] || []).push(b);
    });
    return g;
  }, [activeBlocks]);

  const submitSeason = async (e) => {
    e.preventDefault();
    setSaving(true); setFormError(null);
    try {
      await createSeason(seasonForm);
      setShowSeasonForm(false);
      setSeasonForm({ label: '', start_date: '', end_date: '' });
      await load();
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Erro ao criar temporada');
    } finally { setSaving(false); }
  };

  const submitBlock = async (e) => {
    e.preventDefault();
    setSaving(true); setFormError(null);
    try {
      await createCalendarBlock({
        ...blockForm,
        scope_ref: blockForm.scope === 'CLUB' ? null : blockForm.scope_ref,
        season_id: seasonId || null,
      });
      setShowBlockForm(false);
      setBlockForm({ block_name: '', block_type: 'BASE', start_date: '', end_date: '', scope: 'CLUB', scope_ref: '', objectives: '' });
      setBlocks(await getCalendarBlocks(seasonId || undefined));
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Erro ao criar fase');
    } finally { setSaving(false); }
  };

  const removeBlock = async (blockId) => {
    if (!window.confirm('Cancelar esta fase? O histórico é preservado.')) return;
    try {
      await cancelCalendarBlock(blockId);
      setBlocks(await getCalendarBlocks(seasonId || undefined));
    } catch (err) {
      alert(err?.response?.data?.error || 'Erro ao cancelar fase');
    }
  };

  const input = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm';
  const label = 'block text-xs font-bold text-gray-600 mb-1';

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME.bg }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[220px]">
            <h1 className="text-2xl font-extrabold" style={{ color: THEME.primary }}>
              Calendário da Temporada
            </h1>
            <p className="text-sm text-gray-500">
              Fases definidas aqui valem para todo o clube — Coach, Prep e Nutri recebem a fase do dia automaticamente.
            </p>
          </div>
          <select
            value={seasonId || ''}
            onChange={(e) => setSeasonId(Number(e.target.value) || null)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold"
          >
            {seasons.length === 0 && <option value="">Sem temporadas</option>}
            {seasons.map(s => (
              <option key={s.season_id} value={s.season_id}>
                {s.label} ({fmt(s.start_date)} – {fmt(s.end_date)})
              </option>
            ))}
          </select>
          <button
            onClick={() => { setShowSeasonForm(v => !v); setFormError(null); }}
            className="px-4 py-2 rounded-lg font-bold text-sm border-2"
            style={{ borderColor: THEME.gold, color: THEME.primary }}
          >
            + Temporada
          </button>
          <button
            onClick={() => { setShowBlockForm(v => !v); setFormError(null); }}
            className="px-4 py-2 rounded-lg font-bold text-sm text-white"
            style={{ backgroundColor: THEME.primary }}
          >
            + Fase
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6 text-sm font-semibold">
            {error}
          </div>
        )}

        {/* Season form */}
        {showSeasonForm && (
          <form onSubmit={submitSeason} className="bg-white rounded-xl border border-gray-200 p-4 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
            <div>
              <label className={label}>Nome da temporada</label>
              <input className={input} required placeholder="Temporada 2026/27"
                value={seasonForm.label}
                onChange={e => setSeasonForm(f => ({ ...f, label: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Início</label>
              <input className={input} type="date" required value={seasonForm.start_date}
                onChange={e => setSeasonForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Fim</label>
              <input className={input} type="date" required value={seasonForm.end_date}
                onChange={e => setSeasonForm(f => ({ ...f, end_date: e.target.value }))} />
            </div>
            <button disabled={saving} className="px-4 py-2 rounded-lg font-bold text-sm text-white"
              style={{ backgroundColor: THEME.primary }}>
              {saving ? 'Salvando…' : 'Criar temporada'}
            </button>
            {formError && <div className="sm:col-span-4 text-sm text-red-600 font-semibold">{formError}</div>}
          </form>
        )}

        {/* Block form */}
        {showBlockForm && (
          <form onSubmit={submitBlock} className="bg-white rounded-xl border border-gray-200 p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4 items-end">
            <div className="lg:col-span-2">
              <label className={label}>Nome da fase</label>
              <input className={input} required placeholder="Base — Equipe Principal"
                value={blockForm.block_name}
                onChange={e => setBlockForm(f => ({ ...f, block_name: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Tipo</label>
              <select className={input} value={blockForm.block_type}
                onChange={e => setBlockForm(f => ({ ...f, block_type: e.target.value }))}>
                {PHASE_TYPES.map(t => <option key={t} value={t}>{PHASE_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Início</label>
              <input className={input} type="date" required value={blockForm.start_date}
                onChange={e => setBlockForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Fim</label>
              <input className={input} type="date" required value={blockForm.end_date}
                onChange={e => setBlockForm(f => ({ ...f, end_date: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Escopo</label>
              <select className={input} value={blockForm.scope}
                onChange={e => setBlockForm(f => ({ ...f, scope: e.target.value, scope_ref: '' }))}>
                <option value="CLUB">Clube</option>
                <option value="CATEGORY">Categoria</option>
                <option value="GROUP">Grupo/Squad</option>
              </select>
            </div>
            {blockForm.scope === 'CATEGORY' && (
              <div>
                <label className={label}>Categoria</label>
                {categories.length > 0 ? (
                  <select className={input} required value={blockForm.scope_ref}
                    onChange={e => setBlockForm(f => ({ ...f, scope_ref: e.target.value }))}>
                    <option value="">Selecione…</option>
                    {categories.map(c => (
                      <option key={c.code || c.category} value={c.code || c.category}>
                        {c.name || c.code || c.category}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input className={input} required placeholder="ex: JUVENIL_1"
                    value={blockForm.scope_ref}
                    onChange={e => setBlockForm(f => ({ ...f, scope_ref: e.target.value }))} />
                )}
              </div>
            )}
            {blockForm.scope === 'GROUP' && (
              <div>
                <label className={label}>Squad</label>
                {squads.length > 0 ? (
                  <select className={input} required value={blockForm.scope_ref}
                    onChange={e => setBlockForm(f => ({ ...f, scope_ref: e.target.value }))}>
                    <option value="">Selecione…</option>
                    {squads.map(s => (
                      <option key={s.squad_id} value={s.squad_id}>{s.name}</option>
                    ))}
                  </select>
                ) : (
                  <input className={input} required placeholder="squad_id"
                    value={blockForm.scope_ref}
                    onChange={e => setBlockForm(f => ({ ...f, scope_ref: e.target.value }))} />
                )}
              </div>
            )}
            <button disabled={saving} className="px-4 py-2 rounded-lg font-bold text-sm text-white"
              style={{ backgroundColor: THEME.primary }}>
              {saving ? 'Salvando…' : 'Criar fase'}
            </button>
            {formError && <div className="sm:col-span-3 lg:col-span-6 text-sm text-red-600 font-semibold">{formError}</div>}
          </form>
        )}

        {/* Content */}
        {loading ? (
          <div className="bg-white h-64 rounded-xl animate-pulse" />
        ) : (
          <>
            {season && (
              <div className="text-sm text-gray-500 mb-4 font-semibold">
                {season.label}: {fmt(season.start_date)} → {fmt(season.end_date)}
              </div>
            )}
            {['CLUB', 'CATEGORY', 'GROUP'].map(scopeKey => (
              <div key={scopeKey} className="mb-6">
                <h2 className="text-sm font-extrabold uppercase tracking-wide mb-2" style={{ color: THEME.primary }}>
                  {SCOPE_LABELS[scopeKey]}
                  <span className="ml-2 text-gray-400 font-semibold normal-case">
                    {scopeKey === 'GROUP' ? '(vence sobre categoria e clube)'
                      : scopeKey === 'CATEGORY' ? '(vence sobre clube)' : '(base de todos)'}
                  </span>
                </h2>
                {(byScope[scopeKey] || []).length === 0 ? (
                  <div className="bg-white rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-400">
                    Nenhuma fase definida neste escopo.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {byScope[scopeKey].map(b => (
                      <div key={b.block_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="h-2" style={{ backgroundColor: PHASE_COLORS[b.block_type] || '#6b7280' }} />
                        <div className="p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-extrabold px-2 py-0.5 rounded-full text-white"
                              style={{ backgroundColor: PHASE_COLORS[b.block_type] || '#6b7280' }}>
                              {PHASE_LABELS[b.block_type] || b.block_type}
                            </span>
                            <button onClick={() => removeBlock(b.block_id)}
                              className="text-xs text-gray-400 hover:text-red-500 font-bold">✕</button>
                          </div>
                          <h3 className="text-base font-bold text-gray-900 mt-2">{b.block_name}</h3>
                          <p className="text-sm text-gray-500">{fmt(b.start_date)} → {fmt(b.end_date)}</p>
                          {b.scope !== 'CLUB' && b.scope_ref && (
                            <p className="text-xs text-gray-400 mt-1">
                              {SCOPE_LABELS[b.scope]}: {
                                b.scope === 'GROUP'
                                  ? (squads.find(s => String(s.squad_id) === String(b.scope_ref))?.name || b.scope_ref)
                                  : b.scope_ref
                              }
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default GestorCalendarPage;
